<template>
    <v-container fluid v-if="azienda" style="max-width: 720px">
        <v-row class="align-center mb-2">
            <v-col cols="auto">
                <h2 class="text-h6">{{ azienda.ragioneSociale }}</h2>
            </v-col>
            <v-col cols="auto">
                <v-chip size="small" :color="azienda.attivo ? 'success' : 'grey'" variant="flat">
                    {{ azienda.attivo ? 'Attiva' : 'Disattivata' }}
                </v-chip>
            </v-col>
        </v-row>

        <!-- Un'azienda ha al più un solo utente collegato: pannello singolo invece
             della tabella multi-utente del progetto di riferimento. -->
        <v-card variant="outlined">
            <v-card-title>Utente collegato</v-card-title>
            <v-card-text>
                <template v-if="azienda.utente">
                    <v-row dense>
                        <v-col cols="12" sm="6">
                            <div class="text-caption text-medium-emphasis">Username</div>
                            <div>{{ azienda.utente.username }}</div>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <div class="text-caption text-medium-emphasis">Nome visualizzato</div>
                            <div>{{ azienda.utente.displayName || '—' }}</div>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <div class="text-caption text-medium-emphasis">Email</div>
                            <div>{{ azienda.utente.eMail || '—' }}</div>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <div class="text-caption text-medium-emphasis">Stato</div>
                            <v-chip size="small" :color="azienda.utente.isDeleted ? 'grey' : 'success'" variant="flat">
                                {{ azienda.utente.isDeleted ? 'Disabilitato' : 'Attivo' }}
                            </v-chip>
                        </v-col>
                    </v-row>
                </template>
                <template v-else>
                    <p class="text-body-2 text-medium-emphasis mb-4">
                        Questa azienda non ha ancora un utente collegato.
                    </p>
                    <v-form ref="formUtenteRef">
                        <v-row>
                            <v-col cols="12" sm="6">
                                <v-text-field v-model="formUtente.username" label="Username"
                                    :rules="[v => !!v || 'Obbligatorio']" />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field v-model="formUtente.password" type="password"
                                    label="Password provvisoria"
                                    :rules="[v => (!!v && v.length >= 8) || 'Almeno 8 caratteri']" />
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" sm="6">
                                <v-text-field v-model="formUtente.displayName" label="Nome visualizzato" />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field v-model="formUtente.eMail" label="Email" />
                            </v-col>
                        </v-row>
                    </v-form>
                </template>
            </v-card-text>
            <v-card-actions>
                <v-spacer />
                <template v-if="azienda.utente">
                    <v-btn variant="text" @click="apriDialogPassword">Reset password</v-btn>
                    <v-btn v-if="!azienda.utente.isDeleted" variant="text" color="error" @click="disabilita">
                        Disabilita
                    </v-btn>
                    <v-btn v-else variant="text" color="success" @click="abilita">Riabilita</v-btn>
                </template>
                <v-btn v-else color="primary" @click="creaUtente">Crea utente</v-btn>
            </v-card-actions>
        </v-card>

        <!-- Dialog reset password -->
        <v-dialog v-model="dialogPassword" max-width="420">
            <v-card>
                <v-card-title>Reset password — {{ azienda.utente?.username }}</v-card-title>
                <v-card-text>
                    <v-text-field v-model="nuovaPassword" type="password" label="Nuova password provvisoria"
                        :rules="[v => (!!v && v.length >= 8) || 'Almeno 8 caratteri']" />
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn variant="text" @click="dialogPassword = false">Annulla</v-btn>
                    <v-btn color="primary" @click="confermaResetPassword">Conferma</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
    <v-container v-else>
        <v-progress-circular indeterminate color="primary" />
    </v-container>
</template>

<script>
export default {
    name: "AziendaDettaglioPage",

    props: {
        id: { type: [String, Number], required: true },
    },

    data() {
        return {
            dialogPassword: false,
            nuovaPassword: "",
            formUtente: this.formUtenteVuoto(),
        };
    },

    computed: {
        azienda() {
            return this.$store.getters["aziende/corrente"];
        },
    },

    mounted() {
        this.carica();
    },

    methods: {
        carica() {
            this.$store.dispatch("aziende/caricaDettaglio", this.id);
        },

        formUtenteVuoto() {
            return { username: "", password: "", displayName: "", eMail: "" };
        },

        async creaUtente() {
            if (!this.$refs.formUtenteRef.validate()) return;
            try {
                await this.$store.dispatch("aziende/creaUtente", { id: this.id, dati: this.formUtente });
                this.formUtente = this.formUtenteVuoto();
            } catch (error) {
                console.log(error);
            }
        },

        apriDialogPassword() {
            this.nuovaPassword = "";
            this.dialogPassword = true;
        },

        async confermaResetPassword() {
            if (!this.nuovaPassword || this.nuovaPassword.length < 8) return;
            try {
                await this.$store.dispatch("aziende/resetPasswordUtente", {
                    id: this.id,
                    nuovaPassword: this.nuovaPassword,
                });
                this.dialogPassword = false;
            } catch (error) {
                console.log(error);
            }
        },

        async abilita() {
            try {
                await this.$store.dispatch("aziende/abilitaUtente", { id: this.id });
            } catch (error) {
                console.log(error);
            }
        },

        async disabilita() {
            try {
                await this.$store.dispatch("aziende/disabilitaUtente", { id: this.id });
            } catch (error) {
                console.log(error);
            }
        },
    },
};
</script>
